/**
 * Tasks Page
 *
 * Main page for task management with tabs for different views.
 * Includes Inbox tab for unverified (extracted) tasks to prevent overwhelm.
 */

import { useState } from 'react';
import { CheckSquare, Calendar, AlertTriangle, BarChart3, Plus, Inbox, CheckCheck, Trash2 } from 'lucide-react';
import { useTasks, useTasksToday, useTasksWeek, useTasksOverdue, useTaskStats, useTaskMutations, useTasksInbox } from '@/hooks/use-tasks';
import { TaskList, TaskFiltersBar, TaskEditModal, TaskCreateModal } from '@/components/tasks';
import { Card, CardContent, Button } from '@/components/ui';
import type { Task, TaskFilters, TaskGroupBy, UpdateTaskInput, CreateTaskInput } from '@/types';

type TabType = 'all' | 'inbox' | 'today' | 'week' | 'overdue';

export function TasksPage() {
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [filters, setFilters] = useState<TaskFilters>({});
  const [groupBy, setGroupBy] = useState<TaskGroupBy | undefined>(undefined);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [selectedInboxTasks, setSelectedInboxTasks] = useState<Set<string>>(new Set());

  // Fetch tasks based on active tab
  // Note: 'all' tab only shows verified tasks by default
  const verifiedFilters = { ...filters, verificationStatus: 'verified' as const };
  const allTasksQuery = useTasks(verifiedFilters);
  const inboxQuery = useTasksInbox();
  const todayQuery = useTasksToday();
  const weekQuery = useTasksWeek();
  const overdueQuery = useTasksOverdue();
  const statsQuery = useTaskStats();
  const { createTask, updateTask, completeTask, reopenTask, dismissTask, convertToReference, toggleFocus, verifyTask, bulkVerify, bulkDismiss } = useTaskMutations();

  // Handle create task
  const handleCreateTask = (input: CreateTaskInput) => {
    createTask.mutate(input, {
      onSuccess: () => {
        setShowCreateModal(false);
      },
    });
  };

  // Handle edit task
  const handleEditTask = (task: Task) => {
    setEditingTask(task);
  };

  const handleSaveTask = (taskId: string, updates: UpdateTaskInput) => {
    updateTask.mutate(
      { taskId, input: updates },
      {
        onSuccess: () => {
          setEditingTask(null);
        },
      }
    );
  };

  // Handle complete/reopen task
  const handleCompleteTask = (taskId: string) => {
    completeTask.mutate(taskId);
  };

  const handleReopenTask = (taskId: string) => {
    reopenTask.mutate(taskId);
  };

  const handleDismissTask = (taskId: string) => {
    dismissTask.mutate(taskId);
  };

  const handleConvertToReference = (taskId: string) => {
    convertToReference.mutate(taskId);
  };

  // Handle focus toggle
  const handleFocusToggle = (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      toggleFocus.mutate({ taskId, isFocus: !task.isFocus });
    }
  };

  // Get the right data based on active tab
  const getActiveData = () => {
    switch (activeTab) {
      case 'inbox':
        return {
          tasks: inboxQuery.data?.tasks ?? [],
          isLoading: inboxQuery.isLoading,
          count: inboxQuery.data?.count ?? 0,
        };
      case 'today':
        return {
          tasks: todayQuery.data?.tasks ?? [],
          isLoading: todayQuery.isLoading,
          count: todayQuery.data?.count ?? 0,
        };
      case 'week':
        return {
          tasks: weekQuery.data?.tasks ?? [],
          isLoading: weekQuery.isLoading,
          count: weekQuery.data?.count ?? 0,
        };
      case 'overdue':
        return {
          tasks: overdueQuery.data?.tasks ?? [],
          isLoading: overdueQuery.isLoading,
          count: overdueQuery.data?.count ?? 0,
        };
      default:
        return {
          tasks: allTasksQuery.data?.tasks ?? [],
          isLoading: allTasksQuery.isLoading,
          count: allTasksQuery.data?.total ?? 0,
        };
    }
  };

  const activeData = getActiveData();
  const stats = statsQuery.data;
  
  // Filter out completed tasks if showCompleted is false
  const filteredTasks = showCompleted 
    ? activeData.tasks 
    : activeData.tasks.filter(t => t.status !== 'completed');
  
  const tasks = filteredTasks;
  const isLoading = activeData.isLoading;
  const count = filteredTasks.length;

  // Inbox count from query
  const inboxCount = inboxQuery.data?.count ?? 0;

  const tabs = [
    { id: 'all' as const, label: 'All Tasks', icon: CheckSquare },
    { id: 'inbox' as const, label: 'Inbox', icon: Inbox, count: inboxCount, highlight: inboxCount > 0 },
    { id: 'today' as const, label: 'Today', icon: Calendar, count: stats?.due_today_count },
    { id: 'week' as const, label: 'This Week', icon: Calendar },
    { id: 'overdue' as const, label: 'Overdue', icon: AlertTriangle, count: stats?.overdue_count },
  ];

  // Inbox bulk action handlers
  const handleSelectInboxTask = (taskId: string) => {
    setSelectedInboxTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const handleSelectAllInbox = () => {
    const inboxTasks = inboxQuery.data?.tasks ?? [];
    if (selectedInboxTasks.size === inboxTasks.length) {
      setSelectedInboxTasks(new Set());
    } else {
      setSelectedInboxTasks(new Set(inboxTasks.map((t) => t.id)));
    }
  };

  const handleBulkVerify = () => {
    if (selectedInboxTasks.size === 0) return;
    bulkVerify.mutate(Array.from(selectedInboxTasks), {
      onSuccess: () => {
        setSelectedInboxTasks(new Set());
      },
    });
  };

  const handleBulkDismiss = () => {
    if (selectedInboxTasks.size === 0) return;
    bulkDismiss.mutate(Array.from(selectedInboxTasks), {
      onSuccess: () => {
        setSelectedInboxTasks(new Set());
      },
    });
  };

  const handleVerifyTask = (taskId: string) => {
    verifyTask.mutate(taskId, {
      onSuccess: () => {
        setSelectedInboxTasks((prev) => {
          const next = new Set(prev);
          next.delete(taskId);
          return next;
        });
      },
    });
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
          <p className="text-gray-600 mt-1">
            Manage your tasks and commitments
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Task
        </Button>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CheckSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_tasks}</p>
                <p className="text-sm text-gray-500">Total Tasks</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.due_today_count}</p>
                <p className="text-sm text-gray-500">Due Today</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.overdue_count}</p>
                <p className="text-sm text-gray-500">Overdue</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.by_status?.completed ?? 0}
                </p>
                <p className="text-sm text-gray-500">Completed</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                tab.id === 'overdue'
                  ? 'bg-red-100 text-red-700'
                  : tab.id === 'inbox'
                  ? 'bg-amber-100 text-amber-700 animate-pulse'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Inbox bulk actions bar */}
      {activeTab === 'inbox' && inboxCount > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={selectedInboxTasks.size === (inboxQuery.data?.tasks?.length ?? 0) && selectedInboxTasks.size > 0}
              onChange={handleSelectAllInbox}
              className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
            />
            <span className="text-sm text-amber-800">
              {selectedInboxTasks.size > 0
                ? `${selectedInboxTasks.size} selected`
                : 'Select tasks to triage'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleBulkVerify}
              disabled={selectedInboxTasks.size === 0 || bulkVerify.isPending}
              className="gap-1.5 bg-green-600 hover:bg-green-700"
            >
              <CheckCheck className="w-4 h-4" />
              Verify Selected
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleBulkDismiss}
              disabled={selectedInboxTasks.size === 0 || bulkDismiss.isPending}
              className="gap-1.5 text-red-600 border-red-300 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
              Dismiss Selected
            </Button>
          </div>
        </div>
      )}

      {/* Filters - only show on All tab */}
      {activeTab === 'all' && (
        <div className="mb-4">
          <TaskFiltersBar
            filters={filters}
            onFiltersChange={setFilters}
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            showCompleted={showCompleted}
            onShowCompletedChange={setShowCompleted}
          />
        </div>
      )}

      {/* Results count */}
      <p className="text-sm text-gray-500 mb-4">
        {count} task{count !== 1 ? 's' : ''}
        {activeTab === 'inbox' && ' awaiting triage'}
        {activeTab === 'today' && ' due today'}
        {activeTab === 'week' && ' due this week'}
        {activeTab === 'overdue' && ' overdue'}
      </p>

      {/* Task list */}
      <Card>
        <CardContent className="p-0">
          {activeTab === 'inbox' ? (
            // Inbox view with selection and quick actions
            <div className="divide-y divide-gray-100">
              {isLoading ? (
                <div className="p-8 text-center text-gray-500">Loading inbox...</div>
              ) : tasks.length === 0 ? (
                <div className="p-8 text-center">
                  <Inbox className="w-12 h-12 text-green-300 mx-auto mb-3" />
                  <p className="text-gray-500">Inbox is empty!</p>
                  <p className="text-sm text-gray-400 mt-1">All extracted tasks have been triaged.</p>
                </div>
              ) : (
                tasks.map((task) => (
                  <div
                    key={task.id}
                    className={`flex items-start gap-3 p-4 hover:bg-gray-50 ${
                      selectedInboxTasks.has(task.id) ? 'bg-amber-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedInboxTasks.has(task.id)}
                      onChange={() => handleSelectInboxTask(task.id)}
                      className="mt-1 w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{task.title}</h3>
                      {task.sourceType && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          Source: {task.sourceType} • {new Date(task.createdAt).toLocaleDateString()}
                        </p>
                      )}
                      {task.topicPath && (
                        <p className="text-xs text-gray-500 mt-0.5">📁 {task.topicPath}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleVerifyTask(task.id)}
                        className="text-green-600 hover:bg-green-50"
                        title="Verify this task"
                      >
                        <CheckCheck className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDismissTask(task.id)}
                        className="text-red-600 hover:bg-red-50"
                        title="Dismiss this task"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditTask(task)}
                        className="text-gray-500"
                        title="Edit task"
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <TaskList
              tasks={tasks}
              isLoading={isLoading}
              groupBy={activeTab === 'all' ? groupBy : undefined}
              onEdit={handleEditTask}
              onComplete={handleCompleteTask}
              onReopen={handleReopenTask}
              onFocusToggle={handleFocusToggle}
              emptyMessage={
                activeTab === 'today'
                  ? 'No tasks due today'
                  : activeTab === 'week'
                  ? 'No tasks due this week'
                  : activeTab === 'overdue'
                  ? 'No overdue tasks'
                  : 'No tasks found'
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Create Task Modal */}
      <TaskCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateTask}
        isCreating={createTask.isPending}
      />

      {/* Edit Task Modal - Full featured, same everywhere */}
      {editingTask && (
        <TaskEditModal
          task={editingTask}
          isOpen={!!editingTask}
          onClose={() => setEditingTask(null)}
          onSave={handleSaveTask}
          isSaving={updateTask.isPending}
          showPriorityActions={true}
          onComplete={(taskId) => {
            handleCompleteTask(taskId);
            setEditingTask(null);
          }}
          onDismiss={(taskId) => {
            handleDismissTask(taskId);
            setEditingTask(null);
          }}
          onConvertToReference={(taskId) => {
            handleConvertToReference(taskId);
            setEditingTask(null);
          }}
        />
      )}
    </div>
  );
}
